/**
 * A deadline for the server's Convex queries and mutations.
 *
 * `ConvexHttpClient` calls `fetch` with no signal, so an answer that never
 * comes holds the request until Node's own 300 s header/body timeout — the
 * same 300 s at which Vercel ends the function. Prod 2026-10-06/07: the only
 * two 504s in 72 h were `Task timed out after 300 seconds` on a server action
 * that logged nothing else. Both apps build their clients with
 * {@link convexDeadlineFetch}: `/api/query*` and `/api/mutation` get a
 * deadline, `/api/action` keeps none (an action can legitimately run for
 * minutes).
 *
 * The deadline covers the whole exchange, body included: the abort reason
 * reaches both the `fetch` and the client's `response.json()`.
 *
 * Runtime-neutral (no `server-only`, no Node imports): the shared retry
 * module reads {@link isConvexMutationTimeoutError} from Convex and browser
 * bundles too.
 */

/**
 * A query or mutation runs at most about a second of function time; the rest
 * of a healthy round trip is network and queueing, a few seconds at worst on a
 * cold start. 30 s is an order of magnitude of headroom and still frees the
 * request long before the 300 s platform limit.
 */
export const CONVEX_QUERY_MUTATION_DEADLINE_MS = 30_000

const MUTATION_TIMEOUT_ERROR_NAME = 'ConvexMutationTimeoutError'

type DeadlinedKind = 'query' | 'mutation'

/** The client's query and mutation endpoints, by path suffix. */
const DEADLINED_ENDPOINTS: ReadonlyArray<readonly [string, DeadlinedKind]> = [
  ['/api/query', 'query'],
  ['/api/query_at_ts', 'query'],
  ['/api/query_ts', 'query'],
  ['/api/mutation', 'mutation'],
]

/**
 * `ConvexHttpClient` serializes `{"path":"module:function",…}` with the path
 * first, so an anchored match reads the name without parsing the arguments.
 */
const FUNCTION_PATH_PATTERN = /^\{"path":"([^"]{1,256})"/

/**
 * Our deadline fired before a Convex query answered. Named `TimeoutError`,
 * like `AbortSignal.timeout`'s rejection, so every existing timeout rule
 * applies: not retried by default, replayed under `retryOnTimeout`.
 */
export class ConvexQueryTimeoutError extends Error {
  readonly functionName: string
  readonly timeoutMs: number

  constructor(functionName: string, timeoutMs: number) {
    super(`Convex query ${functionName} got no answer within ${timeoutMs} ms`)
    this.name = 'TimeoutError'
    this.functionName = functionName
    this.timeoutMs = timeoutMs
  }
}

/**
 * Our deadline fired before a Convex mutation answered. The mutation may
 * still have committed, so this is deliberately NOT a `TimeoutError`: a rule
 * that replays a timeout must not replay a write whose outcome is unknown.
 */
export class ConvexMutationTimeoutError extends Error {
  readonly functionName: string
  readonly timeoutMs: number

  constructor(functionName: string, timeoutMs: number) {
    super(
      `Convex mutation ${functionName} got no answer within ${timeoutMs} ms; it may still have committed`
    )
    this.name = MUTATION_TIMEOUT_ERROR_NAME
    this.functionName = functionName
    this.timeoutMs = timeoutMs
  }
}

/** By name, so a second bundled copy of this module still matches. */
export function isConvexMutationTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === MUTATION_TIMEOUT_ERROR_NAME
}

function requestUrl(input: Parameters<typeof globalThis.fetch>[0]): string {
  if (typeof input === 'string') {
    return input
  }
  return input instanceof URL ? input.href : input.url
}

function deadlinedKind(
  input: Parameters<typeof globalThis.fetch>[0]
): DeadlinedKind | undefined {
  let pathname: string
  try {
    pathname = new URL(requestUrl(input)).pathname
  } catch {
    return
  }
  return DEADLINED_ENDPOINTS.find(([suffix]) => pathname.endsWith(suffix))?.[1]
}

function functionNameOf(body: unknown): string {
  return (
    (typeof body === 'string' && FUNCTION_PATH_PATTERN.exec(body)?.[1]) ||
    'unknown'
  )
}

/**
 * A `fetch` for `new ConvexHttpClient(url, { fetch })` that ends a query or
 * mutation exchange after `timeoutMs`, combined with any caller signal.
 * Every other request passes through untouched.
 */
export function createConvexDeadlineFetch(
  options: { timeoutMs?: number; fetch?: typeof globalThis.fetch } = {}
): typeof globalThis.fetch {
  const timeoutMs = options.timeoutMs ?? CONVEX_QUERY_MUTATION_DEADLINE_MS
  return (input, init) => {
    // Read per call, so a fetch stubbed after the client was built applies.
    const baseFetch = options.fetch ?? globalThis.fetch
    const kind = deadlinedKind(input)
    if (!kind) {
      return baseFetch(input, init)
    }
    // Only the name outlives the call: the timer must not pin the body.
    const functionName = functionNameOf(init?.body)
    const deadline = new AbortController()
    const timer = setTimeout(() => {
      deadline.abort(
        kind === 'mutation'
          ? new ConvexMutationTimeoutError(functionName, timeoutMs)
          : new ConvexQueryTimeoutError(functionName, timeoutMs)
      )
    }, timeoutMs)
    // A deadline alone never keeps a process alive.
    ;(timer as { unref?: () => void }).unref?.()
    const release = () => clearTimeout(timer)
    const signal = init?.signal
      ? AbortSignal.any([init.signal, deadline.signal])
      : deadline.signal
    return baseFetch(input, { ...init, signal }).then(
      (response) => releasedWithBody(response, release),
      (error: unknown) => {
        release()
        throw error
      }
    )
  }
}

/**
 * The same answer, with `release` called once its body is read to the end,
 * fails or is cancelled — so a finished call drops its timer at once, while
 * a body that stalls still meets the deadline (the abort errors the source
 * stream, which errors this one with the same reason). The client reads
 * only `ok`, `status` and the body, so rebuilding the `Response` is safe.
 */
function releasedWithBody(response: Response, release: () => void): Response {
  const source = response.body
  if (!source) {
    release()
    return response
  }
  const reader = source.getReader()
  const body = new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        const { done, value } = await reader.read()
        if (done) {
          release()
          controller.close()
          return
        }
        controller.enqueue(value)
      } catch (error) {
        release()
        controller.error(error)
      }
    },
    cancel(reason) {
      release()
      return reader.cancel(reason)
    },
  })
  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  })
}

/** The shared deadline fetch every server `ConvexHttpClient` is built with. */
export const convexDeadlineFetch = createConvexDeadlineFetch()
